/**
 * CourseIdRedirect - Handles legacy /course/:id URLs
 * Fetches the course by ID, then redirects to the slug-based URL
 */
import { useEffect, useState } from 'react';
import { useLocation, useParams, Navigate } from 'react-router-dom';
import { coursesClient } from '../apis/client-instances';
import { Box, CircularProgress } from './ui';

export default function CourseIdRedirect() {
  const { id } = useParams();
  const location = useLocation();
  const [slug, setSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchCourseSlug = async () => {
      if (!id) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const course = await coursesClient.getCourseById(Number(id));
        if (course?.slug) {
          setSlug(course.slug);
        } else {
          // Fallback to id if no slug
          setSlug(id);
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseSlug();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return <Navigate to="/courses" replace />;
  }

  // Preserve query params
  const searchParams = new URLSearchParams(window.location.search);
  const queryString = searchParams.toString();
  const redirectUrl = `/courses/${slug}${queryString ? `?${queryString}` : ''}`;

  // Forward location.state so callers (e.g. EntityLink) can pass back-nav hints
  // through the legacy /course/:id → /courses/:slug redirect.
  return <Navigate to={redirectUrl} replace state={location.state} />;
}
